import config from '@config'
import usePageMetadata from '@hooks/usePageMetadata'
import { useCallback, useMemo } from 'react'
import useTracking from '@hooks/useTracking'
import useViewportTracking from '@hooks/useViewportTracking'

const {
  tracking: { sectionsToBeTrackedOnHomePage },
} = config

const useSectionTracking = (name: string) => {
  const { context, contextId } = usePageMetadata()

  const trackingParams = useMemo(() => {
    if (context === 'home') {
      if (sectionsToBeTrackedOnHomePage.includes(name?.toLowerCase())) {
        return {
          clickParams: {
            event: 'section_click',
            eventAction: 'click',
            eventCategory: 'Section',
            eventLabel: name,
          },
          impressionParams: {
            event: 'section_impression',
            eventAction: 'impression',
            eventCategory: 'Section',
            eventLabel: name,
          },
          track: true,
        }
      }
    } else if (
      context === 'section' &&
      ['/sport/', '/fr/sport/'].includes(contextId)
    ) {
      return {
        clickParams: {
          event: 'sportpage_section_click',
          eventAction: 'section_click',
          eventCategory: 'sportPage',
          eventLabel: name,
        },
        impressionParams: {
          event: 'sportpage_section_impression',
          eventAction: 'section_impression',
          eventCategory: 'sportPage',
          eventLabel: name,
        },
        track: true,
      }
    }

    return {
      clickParams: {},
      impressionParams: {},
      track: false,
    }
  }, [context, contextId, name])

  const onClickTrackingFn = useCallback(
    () => trackingParams.clickParams,
    [trackingParams.clickParams]
  )

  const onImpressionTrackingFn = useCallback(
    () => trackingParams.impressionParams,
    [trackingParams.impressionParams]
  )

  const onImpression = useTracking(onImpressionTrackingFn)

  const onClick = useTracking(onClickTrackingFn)

  const viewportRef = useViewportTracking({
    onImpression: onImpression,
    track: trackingParams.track,
  })

  return {
    onClick: trackingParams.track ? onClick : () => {},
    viewportRef,
  }
}

export default useSectionTracking
